import { useState } from 'react';
import { ActionIcon, Button, CopyButton, Group, Modal, Paper, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';

function ApiKeyValue({ apiKey }: { apiKey: string }) {
  return (
    <TextInput
      readOnly
      value={apiKey}
      styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
      rightSection={
        <CopyButton value={apiKey}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? 'Copied' : 'Copy'}>
              <ActionIcon variant="subtle" color={copied ? 'green' : 'gray'} onClick={copy}>
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      }
    />
  );
}

export function ApiAccessPanel({ organizationId, apiKey }: { organizationId: string; apiKey: string | null }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [rotateModalOpen, setRotateModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const generate = async () => {
    setGenerateError(null);
    setIsGenerating(true);

    const { error } = await api.organizations({ organizationId })['api-key'].post();

    setIsGenerating(false);

    if (error) {
      setGenerateError(getErrorMessage(error, 'Failed to generate API key'));

      return;
    }

    setRotateModalOpen(false);
  };

  const revoke = async () => {
    setRevokeError(null);
    setIsRevoking(true);

    const { error } = await api.organizations({ organizationId })['api-key'].delete();

    setIsRevoking(false);

    if (error) {
      setRevokeError(getErrorMessage(error, 'Failed to revoke API key'));

      return;
    }

    setRevokeModalOpen(false);
  };

  const openRotateModal = () => {
    setGenerateError(null);
    setRotateModalOpen(true);
  };

  const closeRotateModal = () => {
    setGenerateError(null);
    setRotateModalOpen(false);
  };

  const openRevokeModal = () => {
    setRevokeError(null);
    setRevokeModalOpen(true);
  };

  const closeRevokeModal = () => {
    setRevokeError(null);
    setRevokeModalOpen(false);
  };

  return (
    <>
      <Paper p="md" withBorder radius="md">
        <Stack gap="sm">
          <Text fw={600} size="sm">
            API Access
          </Text>
          {apiKey ? (
            <>
              <Text size="sm" c="dimmed">
                This key authenticates external API requests made on behalf of this organization.
              </Text>
              <ApiKeyValue apiKey={apiKey} />
              <Group>
                <Button variant="outline" onClick={openRotateModal}>
                  Rotate
                </Button>
                <Button variant="outline" color="red" onClick={openRevokeModal}>
                  Revoke
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                No API key. Generate one to allow this organization to call the external API.
              </Text>
              <ErrorAlert message={generateError} />
              <div>
                <Button onClick={() => void generate()} loading={isGenerating}>
                  Generate API Key
                </Button>
              </div>
            </>
          )}
        </Stack>
      </Paper>

      <Modal opened={rotateModalOpen} onClose={closeRotateModal} title="Rotate API Key">
        <Stack gap="md">
          <Text>
            Rotating replaces the current API key with a new one. The old key stops working immediately and any
            integration using it will fail until it is updated.
          </Text>
          <ErrorAlert message={generateError} />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={closeRotateModal}>
              Cancel
            </Button>
            <Button onClick={() => void generate()} loading={isGenerating}>
              Rotate API Key
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={revokeModalOpen} onClose={closeRevokeModal} title="Revoke API Key">
        <Stack gap="md">
          <Text>
            Revoking removes the API key for this organization. The key stops working immediately and cannot be
            recovered.
          </Text>
          <ErrorAlert message={revokeError} />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={closeRevokeModal}>
              Cancel
            </Button>
            <Button color="red" onClick={() => void revoke()} loading={isRevoking}>
              Revoke API Key
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
